import {
  Coalition,
  CoalitionApp,
  CoalitionTemplates,
  CoalitionTier,
  CoalitionVar,
  EnvUrls,
  Tier,
} from './types';

// Substitution context passed to renderTemplate. All placeholder
// values live in this flat object — no nested lookups, no eval.
interface Ctx {
  env: string;
  tier: Tier;
  namespace: string;
  cluster: string;
  configRepoOwner: string;
  configRepoName: string;
  configBranch: string;
  vaultMount: string;
  vaultBasePath: string;
  vaultBaseUrl: string;
  jenkinsBaseUrl: string;
  argocdBaseUrl: string;
  argocdAppName: string;
  pipeline: string;
  'app.name': string;
  'app.jenkins': string;
  'app.config': string;
  'app.vault': string;
  'app.repo': string;
  'app.container': string;
  'app.stagingNamespace': string;
  'app.productionNamespace': string;
  'app.stagingArgocdApp': string;
  'app.productionArgocdApp': string;
  // {var.<name>} values from tier, env-override and app `vars`.
  [varKey: `var.${string}`]: string;
}

// Merge template blocks left to right, skipping undefined keys so a
// later block that doesn't set a template never blanks an earlier one.
function mergeTemplates(
  ...blocks: (CoalitionTemplates | undefined)[]
): CoalitionTemplates {
  const out: Record<string, unknown> = {};
  for (const block of blocks) {
    for (const [k, v] of Object.entries(block ?? {})) {
      if (v !== undefined) out[k] = v;
    }
  }
  return out as CoalitionTemplates;
}

// What one env actually renders with: the tier config with that env's
// override applied, plus the effective templates, pipelines and vars.
interface ResolvedEnv {
  tierCfg: CoalitionTier;
  templates: CoalitionTemplates;
  pipelines: string[];
  vars: CoalitionVar[];
}

function resolveEnv(coalition: Coalition, tier: Tier, env: string): ResolvedEnv {
  const base = tier === 'production' ? coalition.production : coalition.staging;
  const o = base.envOverrides?.find(e => e.env === env);
  const tierCfg: CoalitionTier = {
    ...base,
    cluster: o?.cluster ?? base.cluster,
    namespaceOverride: o?.namespace ?? base.namespaceOverride,
    lokiUid: o?.lokiUid ?? base.lokiUid,
    mimirUid: o?.mimirUid ?? base.mimirUid,
    jenkinsBaseUrl: o?.jenkinsBaseUrl ?? base.jenkinsBaseUrl,
    argocdBaseUrl: o?.argocdBaseUrl ?? base.argocdBaseUrl,
  };
  return {
    tierCfg,
    templates: mergeTemplates(coalition.templates, base.templates, o?.templates),
    pipelines: o?.pipelines ?? base.pipelines ?? coalition.pipelines,
    vars: [...(base.vars ?? []), ...(o?.vars ?? [])],
  };
}

// `{placeholder}` substitution. Placeholders unknown to the context
// resolve to empty string — a missing value cascades to an empty URL,
// which the components render as "—".
//
// The key regex is deliberately restricted to alphanumeric + dot + underscore
// (all ctx keys are that shape — `env`, `cluster`, `app.name`, `configRepoOwner`, …).
// The previous `[^}]+` matched greedily up to the first `}`, which
// swallowed the OUTER braces of LogQL queries like
// `{cluster="{cluster}", namespace="{namespace}", container="website"}`
// as a single "key" (`cluster="{cluster`) that missed every ctx entry,
// producing a mangled query and a Grafana Explore iframe with 0 results.
export function renderTemplate(template: string, ctx: Ctx): string {
  return template.replace(/\{([a-zA-Z0-9._]+)\}/g, (_, key) => {
    const v = (ctx as unknown as Record<string, string>)[key];
    return v == null ? '' : String(v);
  });
}

// Resolve the k8s namespace for (app, tier). Priority:
//   1. app.stagingNamespace / app.productionNamespace (per-app override)
//   2. tier.namespaceOverride (per-tier override)
//   3. env name (typical for applications)
function resolveNamespace(
  app: CoalitionApp,
  env: string,
  tier: Tier,
  tierCfg: CoalitionTier,
): string {
  const perApp = tier === 'production' ? app.productionNamespace : app.stagingNamespace;
  if (perApp) return perApp;
  if (tierCfg.namespaceOverride) return tierCfg.namespaceOverride;
  return env;
}

function resolveArgocdAppName(
  app: CoalitionApp,
  tier: Tier,
  templates: CoalitionTemplates,
  ctx: Ctx,
): string {
  // Per-app override wins first (websites use this).
  const perApp = tier === 'production' ? app.productionArgocdApp : app.stagingArgocdApp;
  if (perApp) return perApp;
  // Otherwise render the template.
  return templates.argocdAppName
    ? renderTemplate(templates.argocdAppName, ctx)
    : '';
}

function buildCtx(
  app: CoalitionApp,
  env: string,
  tier: Tier,
  coalition: Coalition,
  resolved: ResolvedEnv,
  pipeline: string,
): Ctx {
  const { tierCfg } = resolved;
  const namespace = resolveNamespace(app, env, tier, tierCfg);
  const ctx: Ctx = {
    env,
    tier,
    namespace,
    cluster: tierCfg.cluster,
    configRepoOwner: coalition.configRepoOwner,
    configRepoName: tierCfg.configRepoName,
    configBranch: coalition.configBranch,
    vaultMount: coalition.vaultMount,
    vaultBasePath: tierCfg.vaultBasePath,
    vaultBaseUrl: coalition.vaultBaseUrl,
    jenkinsBaseUrl: tierCfg.jenkinsBaseUrl,
    argocdBaseUrl: tierCfg.argocdBaseUrl,
    argocdAppName: '', // filled in below
    pipeline,
    'app.name': app.name,
    'app.jenkins': app.jenkins ?? '',
    'app.config': app.config ?? '',
    'app.vault': app.vault ?? '',
    'app.repo': app.repo ?? '',
    // Most services follow `<name>-app`; set `container:` on the app when one doesn't.
    'app.container': app.container ?? `${app.name}-app`,
    'app.stagingNamespace': app.stagingNamespace ?? '',
    'app.productionNamespace': app.productionNamespace ?? '',
    'app.stagingArgocdApp': app.stagingArgocdApp ?? '',
    'app.productionArgocdApp': app.productionArgocdApp ?? '',
  };
  // Tier vars, then env-override vars, then app vars — later wins.
  for (const v of [...resolved.vars, ...(app.vars ?? [])]) {
    ctx[`var.${v.name}`] = v.value;
  }
  // A namespace may use {var.*} when one app's namespace differs per env
  // (ThriveSparrow EU: thrive-eu / thrive-ss-eu vs thrive / thrive-ss in US).
  ctx.namespace = renderTemplate(ctx.namespace, ctx);
  ctx.argocdAppName = resolveArgocdAppName(app, tier, resolved.templates, ctx);
  return ctx;
}

// Wrap a LogQL query in a Grafana Explore panes payload (same URL
// shape every SparrowDesk component has been using).
function lokiExploreUrl(base: string, ds: string, query: string): string {
  const panes = {
    gnt: {
      datasource: ds,
      queries: [{
        refId: 'A',
        expr: query,
        queryType: 'range',
        datasource: { type: 'loki', uid: ds },
        editorMode: 'code',
        direction: 'backward',
      }],
      range: { from: 'now-1h', to: 'now' },
      compact: false,
    },
  };
  return `${base.replace(/\/$/, '')}/explore?schemaVersion=1&panes=${encodeURIComponent(JSON.stringify(panes))}&orgId=1`;
}

// k8s-pod-metrics dashboard URL — no `env` variable in the dashboard
// (verified against /api/dashboards/uid/k8s-pod-metrics), so we don't
// pass var-env.
function metricsUrl(
  base: string,
  dashboardPath: string,
  vars: {
    mimirDs: string;
    cluster: string;
    namespace: string;
    deployment: string;
    eventsLokiDs: string;
  },
): string {
  const params = new URLSearchParams({
    orgId: '1',
    from: 'now-3h',
    to: 'now',
    timezone: 'browser',
    'var-datasource': vars.mimirDs,
    'var-cluster': vars.cluster,
    'var-namespace': vars.namespace,
    'var-deployment': vars.deployment,
    'var-capacity_planning_req_multiplier': '0.9',
    'var-capacity_planning_limit_multiplier': '1.2',
    'var-eventexporter_loki_ds': vars.eventsLokiDs,
  });
  return `${base.replace(/\/$/, '')}/${dashboardPath.replace(/^\//, '')}?${params.toString()}`;
}

// Per-pipeline Jenkins deep-link. The coalition's pipelines[] array
// drives how many of these get rendered by the LinksCard / Jenkins tab.
export interface JenkinsLink {
  pipeline: string;
  url: string;
}

// The main LinkSet the components consume. One row per tool the UI
// wants to render.
export interface LinkSet {
  repo: string;
  feRepo: string;
  config: string;
  vault: string;
  argocd: string;
  argocdAppName: string;
  jenkins: JenkinsLink[];
  logs: string;
  metrics: string;
  manifests: string;
  namespace: string;
  deployment: string;
  cluster: string;
}

export function urlsForApp(
  app: CoalitionApp,
  env: string,
  coalition: Coalition,
): LinkSet | undefined {
  const tier: Tier = coalition.staging.envs.includes(env)
    ? 'staging'
    : coalition.production.envs.includes(env)
      ? 'production'
      : (undefined as unknown as Tier);
  if (!tier) return undefined;

  const resolved = resolveEnv(coalition, tier, env);
  const { tierCfg, templates } = resolved;

  // URL-DRIVEN MODE: coalition has no templates. Entity carries full
  // URLs per tier — the frontend just picks the right one for the
  // current tier and passes it through. No context, no substitution.
  if (!hasAnyTemplate(templates)) {
    return buildUrlDrivenLinkSet(app, tier, tierCfg, env);
  }

  // Base context (pipeline slot empty — filled in per-pipeline below).
  const baseCtx = buildCtx(app, env, tier, coalition, resolved, '');

  // Jenkins deep-links: one URL per pipeline, filtered by app.hasBE /
  // app.hasFE when the pipeline matches those conventional names.
  // `jenkinsLinks` (per-pipeline templates) replaces `jenkins` + pipelines.
  const pipelineTemplates = templates.jenkinsLinks ??
    resolved.pipelines.map(pipeline => ({ pipeline, url: templates.jenkins ?? '' }));
  const jenkins: JenkinsLink[] = pipelineTemplates
    .filter(({ pipeline }) => {
      if (pipeline === 'BackendDeployments' && app.hasBE === false) return false;
      if (pipeline === 'FrontendDeployments' && app.hasFE === false) return false;
      return true;
    })
    .map(({ pipeline, url }) => ({
      pipeline,
      url: url ? renderTemplate(url, { ...baseCtx, pipeline }) : '',
    }));

  const deployment = templates.metricsDeployment
    ? renderTemplate(templates.metricsDeployment, baseCtx)
    : '';

  const logsQuery = templates.logsQuery
    ? renderTemplate(templates.logsQuery, baseCtx)
    : '';

  return {
    namespace: baseCtx.namespace,
    deployment,
    cluster: tierCfg.cluster,
    argocdAppName: baseCtx.argocdAppName,
    repo: app.repo
      ? (templates.repo
          ? renderTemplate(templates.repo, baseCtx)
          : `https://github.com/${coalition.configRepoOwner}/${app.repo}`)
      : '',
    // Frontend repo (when the app has a separate FE codebase). Same
    // URL shape as the backend repo — org from the coalition, name
    // from the app. No template override for feRepo yet; add one if a
    // future coalition needs a non-GitHub source-of-truth.
    feRepo: app.feRepo
      ? `https://github.com/${coalition.configRepoOwner}/${app.feRepo}`
      : '',
    config: templates.config
      ? renderTemplate(templates.config, baseCtx)
      : '',
    vault: templates.vault && app.hasVault !== false
      ? renderTemplate(templates.vault, baseCtx)
      : '',
    argocd: templates.argocd
      ? renderTemplate(templates.argocd, baseCtx)
      : '',
    jenkins,
    logs: logsQuery
      ? lokiExploreUrl(tierCfg.lokiBaseUrl, tierCfg.lokiUid, logsQuery)
      : '',
    metrics: deployment
      ? metricsUrl(tierCfg.mimirBaseUrl, tierCfg.metricsDashboardPath, {
          mimirDs: tierCfg.mimirUid,
          cluster: tierCfg.cluster,
          namespace: baseCtx.namespace,
          deployment,
          eventsLokiDs: tierCfg.eventsLokiUid,
        })
      : '',
    manifests: templates.manifests
      ? renderTemplate(templates.manifests, baseCtx)
      : '',
  };
}

// True when a coalition ships at least one URL template. False for
// URL-driven coalitions (like `sparrowdesk`) where every URL lives on
// the entity itself.
function hasAnyTemplate(t: Coalition['templates'] | undefined): boolean {
  return Boolean(
    t && (t.jenkins || t.jenkinsLinks || t.argocd || t.config || t.vault ||
      t.repo || t.logsQuery || t.metricsDeployment || t.manifests),
  );
}

// A coalition is templated when its root or either tier carries templates.
function isTemplated(coalition: Coalition): boolean {
  return [coalition.templates, coalition.staging.templates, coalition.production.templates]
    .some(hasAnyTemplate);
}

/**
 * The coalition as one entity should see it. URL-driven coalitions (no templates) list both
 * tiers, but a standalone app may only run in one (e.g. a production-only website): keep a
 * tier only when the entity carries at least one `sparrow.io/<tier>-…` annotation, so the env
 * selector never opens on an env with no links. Templated coalitions are returned unchanged;
 * so is the coalition when the entity has no tier annotations at all.
 */
export function coalitionForEntity(
  coalition: Coalition,
  annotations: Record<string, string> | undefined,
): Coalition {
  // An entity may list its own envs per tier (e.g. one production env per
  // region): `sparrow.io/production-envs: US-VIRGINIA,AP-MUMBAI`.
  const declared = (tier: 'staging' | 'production') =>
    annotations?.[`sparrow.io/${tier}-envs`]
      ?.split(',')
      .map(e => e.trim())
      .filter(Boolean);
  if (isTemplated(coalition)) {
    // Templated coalitions: the env list narrows the configured envs (an app
    // that doesn't run in every env, e.g. CRM AI has no on-prem France).
    if (!declared('staging') && !declared('production')) return coalition;
    const narrow = (tier: 'staging' | 'production') => {
      const tierCfg = tier === 'production' ? coalition.production : coalition.staging;
      const only = declared(tier);
      return only ? { ...tierCfg, envs: tierCfg.envs.filter(e => only.includes(e)) } : tierCfg;
    };
    return { ...coalition, staging: narrow('staging'), production: narrow('production') };
  }
  const keys = Object.keys(annotations ?? {});
  const has = (tier: 'staging' | 'production') =>
    keys.some(k => k.startsWith(`sparrow.io/${tier}-`));
  const envsFor = (tier: 'staging' | 'production') => {
    const tierCfg = tier === 'production' ? coalition.production : coalition.staging;
    return declared(tier) ?? (has(tier) ? tierCfg.envs : []);
  };
  if (!has('staging') && !has('production')) return coalition;
  return {
    ...coalition,
    staging: { ...coalition.staging, envs: envsFor('staging') },
    production: { ...coalition.production, envs: envsFor('production') },
  };
}

// URL-driven LinkSet: entity annotations are already the final URLs.
// No template rendering, no pipeline iteration — Jenkins is a single
// "Deploy" button that opens whatever URL the entity provides.
function buildUrlDrivenLinkSet(
  app: CoalitionApp,
  tier: Tier,
  tierCfg: CoalitionTier,
  env: string,
): LinkSet {
  const pick = <T,>(staging: T | undefined, production: T | undefined): T | undefined =>
    tier === 'production' ? production : staging;
  // This env's own URL (`sparrow.io/<env>-<tool>-url`) wins over the tier's.
  // When the entity lists its own envs for this tier, each env carries its
  // own URLs: no tier-level fallback, so a region never shows another
  // region's links.
  const own = app.envUrls?.[env] ?? {};
  const tierFallback = !app.declaredEnvTiers?.includes(tier);
  const pickTier = <T,>(staging: T | undefined, production: T | undefined): T | undefined =>
    tierFallback ? pick(staging, production) : undefined;

  const namespace = pick(app.stagingNamespace, app.productionNamespace) ?? '';
  const jenkinsUrl = own.jenkins ?? pickTier(app.stagingJenkinsUrl, app.productionJenkinsUrl) ?? '';
  const argocdUrl  = own.argocd ?? pickTier(app.stagingArgocdUrl, app.productionArgocdUrl) ?? '';
  const configUrl  = own.config ?? pickTier(app.stagingConfigUrl, app.productionConfigUrl) ?? '';
  const vaultUrl   = own.vault ?? pickTier(app.stagingVaultUrl, app.productionVaultUrl) ?? '';
  const logsUrl    = own.logs ?? pickTier(app.stagingLogsUrl, app.productionLogsUrl) ?? '';
  const metricsUrl = own.metrics ?? pickTier(app.stagingMetricsUrl, app.productionMetricsUrl) ?? '';
  // Label: the app named in this env's own ArgoCD URL, else the tier's app name.
  const argocdApp  = (own.argocd && /\/applications\/[^/]+\/([^/?]+)/.exec(own.argocd)?.[1])
    || pick(app.stagingArgocdApp, app.productionArgocdApp) || '';

  // Jenkins: one implicit "Deploy" pipeline. Entities can carry the
  // whole build/pipeline URL in the annotation — no {app.jenkins},
  // {pipeline}, or {env} substitution.
  const jenkins: JenkinsLink[] = jenkinsUrl
    ? [{ pipeline: 'Deploy', url: jenkinsUrl }]
    : [];

  // Repository URL: prefer `github.com/project-slug` (already `owner/name`).
  // Fall back to bare `sparrow.io/repo` — only useful when it's already
  // a full `owner/name` path, otherwise the URL 404s (missing org).
  const repoUrl = app.slug
    ? `https://github.com/${app.slug}`
    : (app.repo ? `https://github.com/${app.repo}` : '');
  // Frontend repo: inherit org from the slug when present, otherwise
  // use `sparrow.io/frontend-repo` bare.
  const org = app.slug?.split('/')[0];
  const feRepoUrl = app.feRepo
    ? (org ? `https://github.com/${org}/${app.feRepo}` : `https://github.com/${app.feRepo}`)
    : '';

  return {
    namespace,
    deployment: '',                     // not used in URL-driven mode
    cluster: tierCfg.cluster,
    argocdAppName: argocdApp,           // display-only; extracted from URL if user wants
    repo: repoUrl,
    feRepo: feRepoUrl,
    config: configUrl,
    vault: vaultUrl,
    argocd: argocdUrl,
    jenkins,
    logs: logsUrl,
    metrics: metricsUrl,
    manifests: '',                      // URL-driven entities keep manifests in metadata.links
  };
}

// Resolve an entity's app inside a coalition. Two modes:
//
//   1. Legacy "apps-in-config" mode (sparrowdesk-application coalition):
//      coalition.apps is populated; look up by entity name.
//   2. Annotation-driven mode (websites and future standalone
//      Components): coalition.apps is empty; build the app straight
//      from the entity's own `sparrow.io/*` annotations. The
//      catalog-info.yaml in each repo is the single source of truth
//      for that entity's coordinates.
//
// If the entity opts into a coalition but neither mode yields a match,
// the components render their empty-state (dropdown hidden, "—" cells).
export function findApp(
  coalition: Coalition,
  entity: EntityLike,
): CoalitionApp | undefined {
  if (coalition.apps.length > 0) {
    return coalition.apps.find(a => a.name === entity.metadata.name);
  }
  return buildAppFromAnnotations(entity);
}

// Minimal entity shape — the whole `@backstage/catalog-model` Entity
// type is heavier than we need here and only these fields are read.
export interface EntityLike {
  metadata: {
    name: string;
    description?: string;
    annotations?: Record<string, string>;
  };
}

// Annotation vocabulary for standalone Components. Every value below
// is an entity's own coordinate in some external system (namespace,
// ArgoCD app name, Jenkins pipeline segment). The name follows Backstage's
// `<domain>/<key>` convention — same shape as `github.com/project-slug`,
// `backstage.io/kubernetes-namespace`, etc.
const A = {
  jenkins: 'sparrow.io/jenkins-app',
  config: 'sparrow.io/config-path',
  vault: 'sparrow.io/vault-path',
  repo: 'sparrow.io/repo',
  // Frontend repo, when the app has a separate FE codebase.
  feRepo: 'sparrow.io/frontend-repo',
  stagingNamespace: 'sparrow.io/staging-namespace',
  productionNamespace: 'sparrow.io/production-namespace',
  stagingArgocdApp: 'sparrow.io/staging-argocd-app',
  productionArgocdApp: 'sparrow.io/production-argocd-app',
  hasBE: 'sparrow.io/has-be',
  hasFE: 'sparrow.io/has-fe',
  hasVault: 'sparrow.io/has-vault',
  // URL-driven mode annotations. Entity provides full URLs per tier —
  // no coalition-side template assembly. Empty coalition (no `templates:`)
  // means the frontend reads these directly and renders them.
  stagingJenkinsUrl: 'sparrow.io/staging-jenkins-url',
  productionJenkinsUrl: 'sparrow.io/production-jenkins-url',
  stagingArgocdUrl: 'sparrow.io/staging-argocd-url',
  productionArgocdUrl: 'sparrow.io/production-argocd-url',
  stagingConfigUrl: 'sparrow.io/staging-config-url',
  productionConfigUrl: 'sparrow.io/production-config-url',
  stagingVaultUrl: 'sparrow.io/staging-vault-url',
  productionVaultUrl: 'sparrow.io/production-vault-url',
  stagingLogsUrl: 'sparrow.io/staging-logs-url',
  productionLogsUrl: 'sparrow.io/production-logs-url',
  stagingMetricsUrl: 'sparrow.io/staging-metrics-url',
  productionMetricsUrl: 'sparrow.io/production-metrics-url',
} as const;

function parseBool(v: string | undefined): boolean | undefined {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
}

export function buildAppFromAnnotations(entity: EntityLike): CoalitionApp {
  const ann = entity.metadata.annotations ?? {};
  // Fallback: derive repo name from `github.com/project-slug` if the
  // sparrow-specific annotation is absent. Slug is `owner/name`.
  const slug = ann['github.com/project-slug'];
  const repoFromSlug = slug ? slug.split('/').slice(1).join('/') : undefined;
  return {
    name: entity.metadata.name,
    jenkins: ann[A.jenkins],
    config: ann[A.config],
    vault: ann[A.vault],
    repo: ann[A.repo] ?? repoFromSlug,
    slug,                                // full `owner/name` for URL-driven repo button
    feRepo: ann[A.feRepo],
    stagingNamespace: ann[A.stagingNamespace],
    productionNamespace: ann[A.productionNamespace],
    stagingArgocdApp: ann[A.stagingArgocdApp],
    productionArgocdApp: ann[A.productionArgocdApp],
    hasBE: parseBool(ann[A.hasBE]),
    hasFE: parseBool(ann[A.hasFE]),
    hasVault: parseBool(ann[A.hasVault]),
    desc: entity.metadata.description,
    // Full URLs per tier — populated when the entity opts into
    // URL-driven mode (coalition has no `templates:` block).
    stagingJenkinsUrl: ann[A.stagingJenkinsUrl],
    productionJenkinsUrl: ann[A.productionJenkinsUrl],
    stagingArgocdUrl: ann[A.stagingArgocdUrl],
    productionArgocdUrl: ann[A.productionArgocdUrl],
    stagingConfigUrl: ann[A.stagingConfigUrl],
    productionConfigUrl: ann[A.productionConfigUrl],
    stagingVaultUrl: ann[A.stagingVaultUrl],
    productionVaultUrl: ann[A.productionVaultUrl],
    stagingLogsUrl: ann[A.stagingLogsUrl],
    productionLogsUrl: ann[A.productionLogsUrl],
    stagingMetricsUrl: ann[A.stagingMetricsUrl],
    productionMetricsUrl: ann[A.productionMetricsUrl],
    envUrls: envUrlsFromAnnotations(ann),
    declaredEnvTiers: (['staging', 'production'] as const).filter(t => ann[`sparrow.io/${t}-envs`]),
  };
}

// `sparrow.io/<env>-<tool>-url` → { [env]: { [tool]: url } }. The tier-level
// keys (`staging-…`, `production-…`) land under env `staging` / `production`,
// which is what those envs mean in URL-driven coalitions.
const ENV_URL_KEY = /^sparrow\.io\/(.+)-(jenkins|argocd|config|vault|logs|metrics)-url$/;

function envUrlsFromAnnotations(ann: Record<string, string>): Record<string, EnvUrls> {
  const out: Record<string, EnvUrls> = {};
  for (const [key, url] of Object.entries(ann)) {
    const m = ENV_URL_KEY.exec(key);
    if (!m) continue;
    const [, env, tool] = m;
    (out[env] ??= {})[tool as keyof EnvUrls] = url;
  }
  return out;
}
